import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import PageForm from '../../templates/PageForm';
import TextEdit from '../../molecules/TextEdit';
import ATable, { ATableHeader } from '../../organisms/ATable';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { 
    FaFileExcel, FaChevronDown, FaChevronUp, FaChartLine, FaCoins, FaHandHoldingUsd, 
    FaCreditCard, FaHistory, FaBalanceScale, FaCalculator, FaPercentage, FaCalendarAlt,
    FaUser, FaBuilding, FaIdCard, FaPhone, FaMapMarkerAlt, FaDollarSign, FaFileContract,
    FaClipboardList, FaMoneyBillWave, FaGavel, FaShieldAlt, FaExchangeAlt, FaUniversity,
    FaUsers, FaFont, FaHashtag, FaSeedling, FaUserTie, FaMap, FaGlobeAmericas, FaRoad,
    FaCity, FaFlag, FaHome
} from 'react-icons/fa';
import NewTooltip from '../../atoms/NewTooltip';
import { exportToExcel } from '../../../services/ExcelExportService';
import useFetch from '../../../hooks/useFetch';

const API_KEY = process.env.REACT_APP_API_KEY || '';
const BASE_URL = `${process.env.REACT_APP_SICOR_API_URL}/sicor/reports`;

interface ReportItem {
    document: string;
    status: string;
    [key: string]: any;
}

const SicorDocumentPageView: React.FC = () => {
    const { document = '', bacenId = '' } = useParams();
    const navigate = useNavigate();

    const headers = useSignal<ATableHeader[]>([]);
    const rows = useSignal<ReportItem[]>([]);
    const payload = useSignal<any | null>(null);

    const { isLoading, data, error, isTokenError } = useFetch(`${BASE_URL}/${document}/${bacenId}/page-view`, [
        { key: 'x-api-key', value: API_KEY },
    ]);

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    useEffect(() => {
        if (data) {
            // Ajuste para usar o campo 'report' do payload
            const reportData = data.report || {};
            // headers e rows para tabela principal (se necessário)
            headers.value = Object.keys(reportData.acompanhamento_da_operacao?.[0] || {}).map((k) => ({
                id: k,
                title: k,
                field: k,
                canSort: false,
            }));
            rows.value = reportData.acompanhamento_da_operacao || [];
            payload.value = reportData;
        }
    }, [data]);

    const handleExport = () => {
        exportToExcel(headers.value, rows.value, `sicor-${document}-${bacenId}.csv`);
    };

    const formatTitle = (key: string) => {
        // Campos já vêm em português do schema, apenas formatar
        return key
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (l) => l.toUpperCase());
    };

    const getSectionIcon = (key: string) => {
        const lowerKey = key.toLowerCase();
        if (lowerKey.includes('acompanhamento') || lowerKey.includes('operacao')) return <FaChartLine className="mr-2 text-blue-600" />;
        if (lowerKey.includes('saldo') && lowerKey.includes('bonus')) return <FaCoins className="mr-2 text-yellow-600" />;
        if (lowerKey.includes('destinacao') || lowerKey.includes('financiamento')) return <FaHandHoldingUsd className="mr-2 text-green-600" />;
        if (lowerKey.includes('instrumento') || lowerKey.includes('credito')) return <FaCreditCard className="mr-2 text-purple-600" />;
        if (lowerKey.includes('historico') || lowerKey.includes('fonte')) return <FaHistory className="mr-2 text-orange-600" />;
        if (lowerKey.includes('exigibilidade')) return <FaBalanceScale className="mr-2 text-red-600" />;
        if (lowerKey.includes('nuops')) return <FaClipboardList className="mr-2 text-indigo-600" />;
        if (lowerKey.includes('encargos')) return <FaPercentage className="mr-2 text-pink-600" />;
        if (lowerKey.includes('rebate') || lowerKey.includes('bonus')) return <FaMoneyBillWave className="mr-2 text-emerald-600" />;
        return <FaFileContract className="mr-2 text-gray-600" />;
    };

    const getFieldIcon = (key: string, value?: any) => {
        const lowerKey = key.toLowerCase();
        
        // Análise semântica avançada para campos compostos
        if (lowerKey.startsWith('tipo')) {
            if (lowerKey.includes('agricultura') || lowerKey.includes('cultivo') || lowerKey.includes('plantio')) {
                return <FaSeedling className="mr-1 text-green-600 text-xs" />;
            }
            if (lowerKey.includes('juridico') || lowerKey.includes('legal')) {
                return <FaGavel className="mr-1 text-red-600 text-xs" />;
            }
            if (lowerKey.includes('financeiro') || lowerKey.includes('credito')) {
                return <FaDollarSign className="mr-1 text-green-500 text-xs" />;
            }
        }
        
        if (lowerKey.startsWith('porte')) {
            if (lowerKey.includes('produtor') || lowerKey.includes('pessoa') || lowerKey.includes('cliente')) {
                return <FaUserTie className="mr-1 text-blue-600 text-xs" />;
            }
        }
        
        if (lowerKey.startsWith('instrumento')) {
            if (lowerKey.includes('juridico') || lowerKey.includes('legal')) {
                return <FaBalanceScale className="mr-1 text-red-600 text-xs" />;
            }
            if (lowerKey.includes('financeiro') || lowerKey.includes('credito') || lowerKey.includes('pagamento')) {
                return <FaFileContract className="mr-1 text-green-600 text-xs" />;
            }
        }
        
        // Campos com "legal" na semântica jurídica
        if (lowerKey.includes('legal')) {
            return <FaGavel className="mr-1 text-red-600 text-xs" />;
        }
        
        // Campos de localização/endereço
        if (lowerKey.includes('municipio') || lowerKey.includes('cidade')) return <FaCity className="mr-1 text-blue-600 text-xs" />;
        if (lowerKey.includes('estado') || lowerKey.includes('uf')) return <FaFlag className="mr-1 text-red-600 text-xs" />;
        if (lowerKey.includes('pais') || lowerKey.includes('country')) return <FaGlobeAmericas className="mr-1 text-green-600 text-xs" />;
        if (lowerKey.includes('logradouro') || lowerKey.includes('rua') || lowerKey.includes('avenida')) return <FaRoad className="mr-1 text-gray-600 text-xs" />;
        if (lowerKey.includes('endereco') || lowerKey.includes('address')) return <FaHome className="mr-1 text-purple-600 text-xs" />;
        if (lowerKey.includes('localizacao') || lowerKey.includes('location')) return <FaMap className="mr-1 text-orange-600 text-xs" />;
        
        // Ícones específicos por semântica (mantidos)
        if (lowerKey.includes('valor') || lowerKey.includes('montante') || lowerKey.includes('saldo')) return <FaDollarSign className="mr-1 text-green-500 text-xs" />;
        if (lowerKey.includes('data') || lowerKey.includes('vencimento') || lowerKey.includes('prazo')) return <FaCalendarAlt className="mr-1 text-blue-500 text-xs" />;
        if (lowerKey.includes('taxa') || lowerKey.includes('juros') || lowerKey.includes('percentual') || lowerKey.includes('fee')) return <FaPercentage className="mr-1 text-red-500 text-xs" />;
        if (lowerKey.includes('nome') || lowerKey.includes('cliente') || lowerKey.includes('pessoa')) return <FaUser className="mr-1 text-purple-500 text-xs" />;
        if (lowerKey.includes('cnpj') || lowerKey.includes('empresa') || lowerKey.includes('razao')) return <FaBuilding className="mr-1 text-blue-500 text-xs" />;
        if (lowerKey.includes('cpf') || lowerKey.includes('documento') || lowerKey.includes('identidade')) return <FaIdCard className="mr-1 text-orange-500 text-xs" />;
        if (lowerKey.includes('telefone') || lowerKey.includes('contato')) return <FaPhone className="mr-1 text-green-500 text-xs" />;
        if (lowerKey.includes('situacao') || lowerKey.includes('status')) return <FaShieldAlt className="mr-1 text-yellow-500 text-xs" />;
        if (lowerKey.includes('operacao') || lowerKey.includes('transacao')) return <FaExchangeAlt className="mr-1 text-indigo-500 text-xs" />;
        if (lowerKey.includes('instituicao') || lowerKey.includes('agencia') || lowerKey.includes('banco')) return <FaUniversity className="mr-1 text-blue-600 text-xs" />;
        if (lowerKey.includes('individuo') || lowerKey.includes('fisica') || lowerKey.includes('pessoas')) return <FaUsers className="mr-1 text-purple-600 text-xs" />;
        
        // Ícones por tipo de dado
        if (value !== undefined) {
            const stringValue = String(value);
            if (!isNaN(Number(stringValue)) && stringValue.trim() !== '') {
                return <FaHashtag className="mr-1 text-gray-600 text-xs" />;
            }
        }
        
        // Ícone padrão para texto
        return <FaFont className="mr-1 text-gray-500 text-xs" />;
    };

    const formatDocumentValue = (key: string, value: string) => {
        if (!value) return value;
        const lowerKey = key.toLowerCase();
        const cleanValue = value.replace(/\D/g, '');
        
        // Debug para CNPJ
        if (lowerKey.includes('cnpj')) {
            // Debug log removed
        }
        
        if (lowerKey.includes('cpf') && cleanValue.length === 11) {
            return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
        }
        if (lowerKey.includes('cnpj')) {
            if (cleanValue.length === 14) {
                return cleanValue.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
            } else if (cleanValue.length >= 8) {
                // Formatar CNPJ parcial ou incompleto
                return cleanValue.replace(/(\d{2})(\d{3})(\d{3})/, '$1.$2.$3');
            }
        }
        return value;
    };

    const hasContent = (children: React.ReactNode): boolean => {
        if (!children) return false;
        if (React.isValidElement(children)) {
            const props = children.props;
            if (props.children) {
                if (Array.isArray(props.children)) {
                    return props.children.some((child: any) => hasContent(child));
                }
                return hasContent(props.children);
            }
        }
        if (typeof children === 'string') {
            return children.trim().length > 0;
        }
        if (Array.isArray(children)) {
            return children.some(child => hasContent(child));
        }
        return true;
    };

    const SectionCard: React.FC<{ title: string; children: React.ReactNode; iconKey?: string }> = ({ title, children, iconKey = '' }) => {
        const contentExists = hasContent(children);
        const [open, setOpen] = useState(contentExists);
        
        return (
            <div className="mb-4 border rounded shadow">
                <button
                    type="button"
                    className="w-full flex justify-between items-center px-4 py-2 bg-gray-100 hover:bg-gray-200"
                    onClick={() => setOpen(!open)}
                >
                    <span className="font-semibold flex items-center">
                        {getSectionIcon(iconKey || title)}
                        {title}
                    </span>
                    {open ? <FaChevronUp /> : <FaChevronDown />}
                </button>
                <div
                    className={`transition-all duration-300 ease-in-out ${open ? 'opacity-100 max-h-none' : 'opacity-0 max-h-0'} ${open ? 'overflow-visible' : 'overflow-hidden'}`}
                >
                    <div className="p-4 space-y-4 overflow-visible">{children}</div>
                </div>
            </div>
        );
    };

    const renderArray = (key: string, arr: any[]) => {
        if (!arr || arr.length === 0) return null;
        if (typeof arr[0] === 'object') {
            const headers: ATableHeader[] = Object.keys(arr[0]).map((k) => ({
                id: k,
                title: formatTitle(k),
                field: k,
                canSort: false,
            }));
            return (
                <ATable headers={headers}>
                    {arr.map((item, index) => (
                        <tr key={index} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} border-b border-gray-300`}>
                            {headers.map((h) => (
                                <td key={h.id} className="py-2 px-2 text-center">
                                    {formatDocumentValue(h.field as string, String(item[h.field as string] ?? ''))}
                                </td>
                            ))}
                        </tr>
                    ))}
                </ATable>
            );
        }
        return (
            <ATable headers={[{ id: 'valor', title: formatTitle(key), field: 'valor', canSort: false }]}>
                {arr.map((value, index) => (
                    <tr key={index} className={`${index % 2 === 0 ? 'bg-slate-100' : ''} border-b border-gray-300`}>
                        <td className="py-2 px-2 text-center">{value}</td>
                    </tr>
                ))}
            </ATable>
        );
    };

    const renderObject = (obj: Record<string, any>) => (
        <div className="grid grid-cols-1 gap-4 overflow-visible">{Object.entries(obj).map(([k, v]) => renderValue(k, v))}</div>
    );

    const renderValue = (key: string, value: any) => {
        if (Array.isArray(value)) {
            // Só renderiza se o array tem conteúdo válido
            if (value.length === 0) return null;
            return (
                <div key={key} className="col-span-12">
                    <SectionCard title={formatTitle(key)} iconKey={key}>{renderArray(key, value)}</SectionCard>
                </div>
            );
        }
        if (value && typeof value === 'object') {
            // Só renderiza se o objeto tem propriedades com valores válidos
            const hasValidContent = Object.values(value).some(v => 
                v !== null && v !== undefined && v !== '' && 
                (Array.isArray(v) ? v.length > 0 : true)
            );
            if (!hasValidContent) return null;
            
            return (
                <div key={key} className="col-span-12">
                    <SectionCard title={formatTitle(key)} iconKey={key}>{renderObject(value)}</SectionCard>
                </div>
            );
        }
        // Só renderiza campos com valores válidos
        if (value === null || value === undefined || value === '') return null;
        
        const fieldIcon = getFieldIcon(key, value);
        const formattedValue = formatDocumentValue(key, String(value ?? ''));
        return (
            <div key={key} className="col-span-4">
                <div className="flex items-center">
                    {fieldIcon}
                    <TextEdit label={formatTitle(key)} value={formattedValue} disabled className="flex-1" />
                </div>
            </div>
        );
    };

    return (
        <PageForm
            pageTitle="Relatório Sicor"
            apiVersion="v1"
            isLoading={isLoading}
            breadcrumb={[
                { label: 'Relatórios Sicor', url: '/sicor/report/0/20/document/DESC' },
                { label: `${document}`, url: `/sicor/document/${document}/0/20/bank/ASC` },
                { label: 'Detalhe do relatório', url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end">
                    <button
                        className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded"
                        onClick={() => navigate(-1)}
                    >
                        <IoMdArrowRoundBack />
                    </button>
                    <NewTooltip content="Exportar para Excel" alignment="left">
                        <button
                            type="button"
                            className="text-white bg-blue-500 hover:bg-blue-700 font-medium rounded text-sm px-4 py-2 ml-2 flex items-center"
                            onClick={handleExport}
                        >
                            <FaFileExcel className="mr-2" />
                            Exportar
                        </button>
                    </NewTooltip>
                </div>
            }
        >
            {payload.value && <div className="grid grid-cols-1 gap-4 overflow-visible min-h-0">{renderObject(payload.value)}</div>}
        </PageForm>
    );
};

export default SicorDocumentPageView;

